import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import { useSectionSpecAtTime } from "@/shared/hooks/useSectionSpec";
import type { M1InspectionFormProps } from "../../components/M1InspectionForm";
import {
	createFormDraft,
	reconcileFormDraft,
	type InspectionFormDraft,
} from "../../utils/inspectionDraft";
import { validateDraftForSave } from "../../utils/inspectionValidation";
import type { SectionSpecDetail } from "@/shared/models/SectionSpec";
import type { InspectionWithValues } from "@/pages/products/test-shack/models/Inspection";
import type { InspectionFormSubmission } from "@/pages/products/test-shack/models/InspectionForm";

/**
 * Identifies the saved version of a sample. Refetches return new objects for the
 * same data, so comparing references would reset unsaved edits on every refetch.
 * UpdatedAt changes after a save, which resets the form to the saved values.
 */
const sessionKeyOf = (inspection: InspectionWithValues | null) =>
	inspection
		? `${inspection.ProfileInspectionId}:${String(inspection.UpdatedAt ?? inspection.CreatedAt)}`
		: null;

interface SheetInspectionFormProps {
	assetId: number;
	onSpecChange: (detail: SectionSpecDetail | null) => void;
	editing: InspectionWithValues | null;
	saving: boolean;
	onCancelEdit: () => void;
	onSubmit: (payload: InspectionFormSubmission) => void;
}

/** Sheet form state and props; the page renders M1InspectionForm with them. */
export const useSheetInspectionForm = ({
	assetId,
	editing,
	onSpecChange,
	saving,
	onCancelEdit,
	onSubmit,
}: SheetInspectionFormProps): Omit<M1InspectionFormProps, "layout"> => {
	const form = useForm<InspectionFormDraft>({
		defaultValues: createFormDraft(editing),
		shouldUnregister: false,
	});
	const sectionId = useWatch({ control: form.control, name: "sectionId" });
	const inspectedAt = useWatch({ control: form.control, name: "inspectedAt" });
	const query = useSectionSpecAtTime(sectionId, assetId, inspectedAt);
	const lookupReady =
		query.isSuccess &&
		!query.isPreviousData &&
		!query.isFetching &&
		query.isInspectionTimeSettled;
	const detail = query.data ?? null;
	const spec = detail?.spec ?? null;
	const items = detail?.items ?? [];
	const sessionKey = sessionKeyOf(editing);
	const [resolved, setResolved] = useState({
		sessionKey,
		id: editing?.ProfileSpecId ?? null,
	});
	const sessionChanged = resolved.sessionKey !== sessionKey;
	const revisionChanged =
		lookupReady && resolved.id !== (spec?.ProfileSpecId ?? null);
	const { reset, setValue } = form;

	useEffect(() => {
		if (sessionChanged) {
			reset(createFormDraft(editing));
			setResolved({ sessionKey, id: editing?.ProfileSpecId ?? null });
			return;
		}
		if (!revisionChanged) return;
		const next = reconcileFormDraft(form.getValues(), items);
		setValue("readings", next.readings, { shouldDirty: true });
		setValue("angles", next.angles, { shouldDirty: true });
		setResolved({ sessionKey, id: spec?.ProfileSpecId ?? null });
	}, [
		editing,
		sessionKey,
		sessionChanged,
		revisionChanged,
		reset,
		setValue,
		form,
		items,
		spec?.ProfileSpecId,
	]);

	const ready =
		lookupReady &&
		!!spec &&
		spec.SectionId === sectionId &&
		!sessionChanged &&
		!revisionChanged;
	useEffect(() => {
		onSpecChange(ready ? detail : null);
	}, [onSpecChange, ready, detail]);

	const submit = (draft: InspectionFormDraft) => {
		if (
			!ready ||
			saving ||
			!form.formState.isDirty ||
			sectionId === null ||
			!draft.inspectedAt
		)
			return;
		const validation = validateDraftForSave(
			draft,
			items,
			detail?.weightProfileItemId ?? null,
		);
		if (!validation.ok) {
			form.setError(validation.field, { message: validation.message });
			return;
		}
		onSubmit({
			sectionId,
			heatId: validation.heatId,
			inspectedAt: draft.inspectedAt,
			measuredLbsPerFoot: validation.weight,
			comments: draft.comments.trim() || null,
			readings: validation.readings,
		});
	};

	return {
		assetId,
		form,
		sectionId,
		items,
		editing,
		spec,
		weightProfileItemId: detail?.weightProfileItemId ?? null,
		canSave: ready && form.formState.isDirty,
		specError: query.isError
			? "The specification for the selected sample time could not be loaded."
			: undefined,
		loading:
			query.isFetching || !query.isInspectionTimeSettled || sessionChanged,
		hasSpec: ready,
		specMissing: lookupReady && !spec,
		saving,
		onSubmit: form.handleSubmit(submit),
		onCancel: onCancelEdit,
	};
};
